import { EquipmentDisplay } from '@components/helpers/EquipmentDisplay';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useAppStore } from '@stores';
import * as Shared from '@shared/lib';

export function EquipmentExp({ xp }: { xp: number }) {
  const [chassisExp, setChassisExp] = useState(0);
  const [weaponExp, setWeaponExp] = useState(0);

  const { t } = useTranslation(undefined, { keyPrefix: 'Game.GameEnded' });
  const { userData } = useAppStore();

  const equippedChassis = userData.chassis.find(c => c.active);
  const equippedWeapon = userData.weapons.find(w => w.active);

  const cData = Shared.chassisData.find(c => c.key === equippedChassis?.key);
  const wData = Shared.weaponsData.find(w => w.key === equippedWeapon?.key);

  useEffect(() => {
    if (!equippedChassis || !equippedWeapon) return;
    setChassisExp(equippedChassis.xp);
    setWeaponExp(equippedWeapon.xp);
    const interval = setInterval(() => {
      setChassisExp(x => {
        const newExp = Math.min(x + 1, equippedChassis.xp + xp);
        if (newExp === equippedChassis.xp + xp) clearInterval(interval);
        return newExp;
      });
      setWeaponExp(x => {
        const newExp = Math.min(x + 1, equippedWeapon.xp + xp);
        if (newExp === equippedWeapon.xp + xp) clearInterval(interval);
        return newExp;
      });
    }, 5);
    return () => clearInterval(interval);
  }, [equippedChassis, equippedWeapon]);

  if (!cData || !wData || !equippedChassis || !equippedWeapon) return null;

  const cLevel = Shared.ChassisLevelCalculator.calc(chassisExp);
  const cLevelProgress = Math.min(100, (chassisExp - cLevel.base) / cLevel.toNext * 100);

  const wLevel = Shared.WeaponLevelCalculator.calc(weaponExp);
  const wLevelProgress = Math.min(100, (weaponExp - wLevel.base) / wLevel.toNext * 100);

  const cGained = chassisExp - equippedChassis.xp;
  const wGained = weaponExp - equippedWeapon.xp;

  return (
    <>
      <EquipmentDisplay value={cData.key} type="CHASSIS" noBlur>
        <p className="text-mono text-sm text-white">{t('gainedExp', { exp: cGained })}</p>
        <div className="w-full h-2 bg-tertiary my-1 relative">
          <div style={{ width: `${cLevelProgress}%` }} className="h-full bg-primary" />
        </div>
        <div className="flex items-center">
          <p className="text-mono text-sm text-white">{t('level', { level: cLevel.current })}</p>
          {cLevel.current >= 30 && (
            <p className="text-sm active ml-2">{t('upgradeAvailable')}</p>
          )}
        </div>
      </EquipmentDisplay>

      <EquipmentDisplay className="border-y border-secondary" value={wData.key} type="WEAPON" noBlur>
        <p className="text-mono text-sm text-white">{t('gainedExp', { exp: wGained })}</p>
        <div className="w-full h-2 bg-tertiary my-1 relative">
          <div style={{ width: `${wLevelProgress}%` }} className="h-full bg-primary" />
        </div>
        <div className="flex items-center">
          <p className="text-mono text-sm text-white">{t('level', { level: wLevel.current })}</p>
          {wLevel.current >= 30 && (
            <p className="text-sm active ml-2">{t('upgradeAvailable')}</p>
          )}
        </div>
      </EquipmentDisplay>
    </>
  );
}